import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../services/api';
import PageHeader from '../../../components/common/PageHeader';
import AlertToast from '../../../components/common/AlertToast';

const _serverBase = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
function uploadUrl(p) {
  if (!p) return '';
  if (p.startsWith('http')) return p;
  return `${_serverBase}${p}`;
}
function toEmbedUrl(url) {
  if (!url) return '';
  if (url.includes('/embed/')) return url;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : url;
}

// ─── Image Upload Widget ──────────────────────────────────────────────────────
function ImageUpload({ value, onChange, label = 'Image', maxBytes = null }) {
  const [uploading, setUploading] = useState(false);
  // localBlob: set when user picks a file — never overwritten by server fetch
  // serverBlob: fetched from server when opening an existing record
  const [localBlob, setLocalBlob] = useState('');
  const [serverBlob, setServerBlob] = useState('');
  const inputRef = useRef();

  // When value changes (form loaded with existing record), fetch image from server
  useEffect(() => {
    // If user just uploaded, we already have localBlob — no need to re-fetch
    if (localBlob) return;
    if (!value) { setServerBlob(''); return; }
    if (value.startsWith('http') || value.startsWith('blob:') || value.startsWith('data:')) {
      setServerBlob(value);
      return;
    }
    // Showcase is public — use the URL directly, no auth fetch needed
    setServerBlob(uploadUrl(value));
  }, [value, localBlob]);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (maxBytes && file.size > maxBytes) {
      alert(`${label} must be under ${Math.round(maxBytes / (1024 * 1024))} MB.`);
      e.target.value = '';
      return;
    }
    // Show local preview immediately — zero latency, no server needed
    const blob = URL.createObjectURL(file);
    setLocalBlob(blob);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const r = await api.post('/training/promotions/upload-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(r.data?.data?.url || r.data?.url || '');
    } catch (err) {
      setLocalBlob('');
      alert('Upload failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const previewSrc = localBlob || serverBlob;

  return (
    <div>
      {previewSrc && (
        <div className="mb-2" style={{ position: 'relative', display: 'inline-block' }}>
          <img src={previewSrc} alt="preview"
            style={{ height: 120, maxWidth: 320, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0', display: 'block' }} />
          <button type="button" className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 p-0"
            style={{ width: 22, height: 22, lineHeight: 1 }}
            onClick={() => { setLocalBlob(''); setServerBlob(''); onChange(''); }}>
            <i className="bi bi-x" style={{ fontSize: 13 }} />
          </button>
          {uploading && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.6)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span className="spinner-border spinner-border-sm text-primary" />
            </div>
          )}
        </div>
      )}
      <div className="d-flex gap-2 align-items-center flex-wrap">
        <button type="button" className="btn btn-outline-secondary btn-sm" disabled={uploading}
          onClick={() => inputRef.current?.click()}>
          {uploading
            ? <><span className="spinner-border spinner-border-sm me-1" />Uploading…</>
            : <><i className="bi bi-image me-1" />{previewSrc ? 'Change' : 'Upload'} {label}</>
          }
        </button>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" style={{ display: 'none' }} onChange={handleFile} />
        <span className="text-muted small">or paste URL</span>
        <input className="form-control form-control-sm" style={{ maxWidth: 240 }}
          placeholder="https://…" value={value || ''} onChange={e => { setLocalBlob(''); setServerBlob(''); onChange(e.target.value); }} />
      </div>
    </div>
  );
}

// ─── Forms ────────────────────────────────────────────────────────────────────
function StarPicker({ value, onChange }) {
  return (
    <div className="d-flex gap-1">
      {[1, 2, 3, 4, 5].map(s => (
        <button key={s} type="button" className="btn p-0 border-0"
          style={{ fontSize: 24, color: (value || 5) >= s ? '#f59e0b' : '#d1d5db', lineHeight: 1 }}
          onClick={() => onChange(s)}>★</button>
      ))}
    </div>
  );
}

function CarouselForm({ form, onChange }) {
  const set = (k, v) => onChange(f => ({ ...f, [k]: v }));
  return (
    <div className="row g-3">
      <div className="col-12">
        <label className="form-label fw-semibold">Slide Image <span className="text-danger">*</span></label>
        <ImageUpload value={form.imageUrl || ''} onChange={v => set('imageUrl', v)} label="Slide Image" />
        <div className="form-text">Recommended: 1200 × 400 px landscape. JPG, PNG, WebP.</div>
      </div>
      <div className="col-md-4">
        <label className="form-label fw-semibold">Order</label>
        <input type="number" className="form-control" value={form.order ?? 0} min={0}
          onChange={e => set('order', parseInt(e.target.value) || 0)} />
      </div>
      <div className="col-auto d-flex align-items-end">
        <div className="form-check form-switch">
          <input className="form-check-input" type="checkbox" id="csActive" checked={!!form.isActive}
            onChange={e => set('isActive', e.target.checked)} />
          <label className="form-check-label" htmlFor="csActive">Active</label>
        </div>
      </div>
    </div>
  );
}

function VideoReviewForm({ form, onChange }) {
  const set = (k, v) => onChange(f => ({ ...f, [k]: v }));
  return (
    <div className="row g-3">
      <div className="col-md-6">
        <label className="form-label fw-semibold">Student Name <span className="text-danger">*</span></label>
        <input className="form-control" value={form.reviewerName || ''} onChange={e => set('reviewerName', e.target.value)}
          placeholder="e.g. Priya Sharma" />
      </div>
      <div className="col-md-6">
        <label className="form-label fw-semibold">Course</label>
        <input className="form-control" value={form.reviewerCourse || ''} onChange={e => set('reviewerCourse', e.target.value)}
          placeholder="e.g. Full Stack Web Development" />
      </div>
      <div className="col-md-6">
        <label className="form-label fw-semibold">Batch / Year</label>
        <input className="form-control" value={form.reviewerBatch || ''} onChange={e => set('reviewerBatch', e.target.value)}
          placeholder="e.g. Batch 2024-B" />
      </div>
      <div className="col-md-6">
        <label className="form-label fw-semibold">Rating</label>
        <StarPicker value={form.rating || 5} onChange={v => set('rating', v)} />
      </div>
      <div className="col-12">
        <label className="form-label fw-semibold">Video URL <span className="text-danger">*</span></label>
        <input className="form-control" value={form.videoUrl || ''} onChange={e => set('videoUrl', e.target.value)}
          placeholder="Paste any YouTube URL (watch, share, or embed)" />
        <div className="form-text">
          Paste any YouTube URL — watch link, share link, or embed URL. Preview will appear below.
        </div>
        {form.videoUrl && toEmbedUrl(form.videoUrl) && (
          <div className="mt-2 rounded overflow-hidden border" style={{ position: 'relative', paddingBottom: '56.25%', background: '#000' }}>
            <iframe src={toEmbedUrl(form.videoUrl)} title="Video preview" allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }} />
          </div>
        )}
      </div>
      <div className="col-12">
        <label className="form-label fw-semibold">Thumbnail Image</label>
        <ImageUpload value={form.thumbnailUrl || ''} onChange={v => set('thumbnailUrl', v)} label="Thumbnail" />
        <div className="form-text">For YouTube: <code>https://img.youtube.com/vi/VIDEO_ID/hqdefault.jpg</code></div>
      </div>
      <div className="col-12">
        <label className="form-label fw-semibold">Review Quote</label>
        <textarea className="form-control" rows={2} value={form.reviewText || ''} onChange={e => set('reviewText', e.target.value)}
          placeholder="A short quote from the student…" />
      </div>
      <div className="col-md-4">
        <label className="form-label fw-semibold">Order</label>
        <input type="number" className="form-control" value={form.order ?? 0} min={0}
          onChange={e => set('order', parseInt(e.target.value) || 0)} />
      </div>
      <div className="col-auto">
        <div className="form-check form-switch mt-2">
          <input className="form-check-input" type="checkbox" id="vrActive" checked={!!form.isActive}
            onChange={e => set('isActive', e.target.checked)} />
          <label className="form-check-label" htmlFor="vrActive">Active</label>
        </div>
      </div>
    </div>
  );
}

function PromotionForm({ form, onChange }) {
  const set = (k, v) => onChange(f => ({ ...f, [k]: v }));
  return (
    <div className="row g-3">
      <div className="col-12">
        <label className="form-label fw-semibold">Poster / Banner Image</label>
        <ImageUpload value={form.imageUrl || ''} onChange={v => set('imageUrl', v)} label="Poster" />
        <div className="form-text">Recommended: 1200 × 600 px. Shown as the main visual.</div>
      </div>
      <div className="col-12">
        <label className="form-label fw-semibold">Title</label>
        <input className="form-control" value={form.promoTitle || ''} onChange={e => set('promoTitle', e.target.value)}
          placeholder="e.g. Summer Intensive Batch — Limited Seats!" />
      </div>
      <div className="col-12">
        <label className="form-label fw-semibold">Description / Text</label>
        <textarea className="form-control" rows={2} value={form.promoText || ''} onChange={e => set('promoText', e.target.value)}
          placeholder="Additional details about this promotion…" />
      </div>
      <div className="col-12">
        <label className="form-label fw-semibold">Promotion Video URL (optional)</label>
        <input className="form-control" value={form.promoVideoUrl || ''} onChange={e => set('promoVideoUrl', e.target.value)}
          placeholder="Paste any YouTube URL (watch, share, or embed)" />
        {form.promoVideoUrl && toEmbedUrl(form.promoVideoUrl) && (
          <div className="mt-2 rounded overflow-hidden border" style={{ position: 'relative', paddingBottom: '56.25%', background: '#000' }}>
            <iframe src={toEmbedUrl(form.promoVideoUrl)} title="Video preview" allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }} />
          </div>
        )}
      </div>
      <div className="col-md-6">
        <label className="form-label fw-semibold">Button Text</label>
        <input className="form-control" value={form.ctaText || ''} onChange={e => set('ctaText', e.target.value)}
          placeholder="e.g. Enroll Now, Learn More" />
      </div>
      <div className="col-md-6">
        <label className="form-label fw-semibold">Button Link</label>
        <input className="form-control" value={form.ctaLink || ''} onChange={e => set('ctaLink', e.target.value)}
          placeholder="https://…" />
      </div>
      <div className="col-md-4">
        <label className="form-label fw-semibold">Order</label>
        <input type="number" className="form-control" value={form.order ?? 0} min={0}
          onChange={e => set('order', parseInt(e.target.value) || 0)} />
      </div>
      <div className="col-auto">
        <div className="form-check form-switch mt-2">
          <input className="form-check-input" type="checkbox" id="pmActive" checked={!!form.isActive}
            onChange={e => set('isActive', e.target.checked)} />
          <label className="form-check-label" htmlFor="pmActive">Active</label>
        </div>
      </div>
    </div>
  );
}

function LearningEventForm({ form, onChange }) {
  const set = (key, value) => onChange(current => ({ ...current, [key]: value }));
  const isOnline = ['online', 'hybrid'].includes(form.deliveryMode || 'online');
  const isOffline = ['offline', 'hybrid'].includes(form.deliveryMode || 'online');
  return (
    <div className="row g-3">
      <div className="col-md-6">
        <label className="form-label fw-semibold">Content Type <span className="text-danger">*</span></label>
        <select className="form-select" value={form.eventKind || 'free_live_class'} onChange={e => set('eventKind', e.target.value)}>
          <option value="free_live_class">Free Live Class</option>
          <option value="workshop">Workshop</option>
          <option value="webinar">Webinar</option>
          <option value="event">Event</option>
        </select>
      </div>
      <div className="col-md-6">
        <label className="form-label fw-semibold">Learning Mode <span className="text-danger">*</span></label>
        <select className="form-select" value={form.deliveryMode || 'online'} onChange={e => set('deliveryMode', e.target.value)}>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
          <option value="hybrid">Hybrid</option>
        </select>
      </div>
      <div className="col-12">
        <label className="form-label fw-semibold">Title <span className="text-danger">*</span></label>
        <input className="form-control" value={form.promoTitle || ''} onChange={e => set('promoTitle', e.target.value)} placeholder="e.g. Free Python Career Starter Class" />
      </div>
      <div className="col-12">
        <label className="form-label fw-semibold">Short Description <span className="text-danger">*</span></label>
        <textarea className="form-control" rows={2} value={form.promoText || ''} onChange={e => set('promoText', e.target.value)} placeholder="What will students learn or gain?" />
      </div>
      <div className="col-12">
        <label className="form-label fw-semibold">Thumbnail <span className="text-danger">*</span></label>
        <ImageUpload value={form.imageUrl || ''} onChange={value => set('imageUrl', value)} label="Thumbnail" maxBytes={2 * 1024 * 1024} />
        <div className="form-text"><strong>Required:</strong> 1200 x 675 px (16:9), JPG/PNG/WebP, under 2 MB. Keep important content near the centre for mobile cropping.</div>
      </div>
      <div className="col-md-4">
        <label className="form-label fw-semibold">Date <span className="text-danger">*</span></label>
        <input type="date" className="form-control" value={form.eventDate || ''} onChange={e => set('eventDate', e.target.value)} />
      </div>
      <div className="col-md-4">
        <label className="form-label fw-semibold">Start Time <span className="text-danger">*</span></label>
        <input type="time" className="form-control" value={form.startTime || ''} onChange={e => set('startTime', e.target.value)} />
      </div>
      <div className="col-md-4">
        <label className="form-label fw-semibold">End Time</label>
        <input type="time" className="form-control" value={form.endTime || ''} onChange={e => set('endTime', e.target.value)} />
      </div>
      {isOnline && <div className="col-md-6">
        <label className="form-label fw-semibold">Meeting Link</label>
        <input className="form-control" value={form.meetingUrl || ''} onChange={e => set('meetingUrl', e.target.value)} placeholder="Google Meet, Zoom or Teams URL" />
      </div>}
      {isOffline && <div className="col-md-6">
        <label className="form-label fw-semibold">Venue</label>
        <input className="form-control" value={form.venue || ''} onChange={e => set('venue', e.target.value)} placeholder="Campus, hall or classroom" />
      </div>}
      <div className="col-md-4">
        <label className="form-label fw-semibold">Capacity</label>
        <input type="number" min="0" className="form-control" value={form.capacity ?? 0} onChange={e => set('capacity', Math.max(0, Number(e.target.value || 0)))} />
      </div>
      <div className="col-md-8">
        <label className="form-label fw-semibold">Registration Link</label>
        <input className="form-control" value={form.registrationUrl || ''} onChange={e => set('registrationUrl', e.target.value)} placeholder="Optional external URL or application route" />
      </div>
      <div className="col-md-4">
        <label className="form-label fw-semibold">Display Order</label>
        <input type="number" min="0" className="form-control" value={form.order ?? 0} onChange={e => set('order', parseInt(e.target.value, 10) || 0)} />
      </div>
      <div className="col-auto d-flex align-items-end">
        <div className="form-check form-switch mb-2">
          <input className="form-check-input" type="checkbox" id="learningEventActive" checked={!!form.isActive} onChange={e => set('isActive', e.target.checked)} />
          <label className="form-check-label" htmlFor="learningEventActive">Published</label>
        </div>
      </div>
    </div>
  );
}

function CertificateForm({ form, onChange }) {
  const set = (k, v) => onChange(f => ({ ...f, [k]: v }));
  return (
    <div className="row g-3">
      <div className="col-md-6">
        <label className="form-label fw-semibold">Student Name <span className="text-danger">*</span></label>
        <input className="form-control" value={form.studentName || ''} onChange={e => set('studentName', e.target.value)} placeholder="e.g. Priya Sharma" />
      </div>
      <div className="col-md-6">
        <label className="form-label fw-semibold">Course Name <span className="text-danger">*</span></label>
        <input className="form-control" value={form.courseName || ''} onChange={e => set('courseName', e.target.value)} placeholder="e.g. Full Stack Development" />
      </div>
      <div className="col-md-6">
        <label className="form-label fw-semibold">Batch / Year</label>
        <input className="form-control" value={form.batchName || ''} onChange={e => set('batchName', e.target.value)} placeholder="e.g. Batch 2024-A" />
      </div>
      <div className="col-md-6">
        <label className="form-label fw-semibold">Issued Date</label>
        <input type="date" className="form-control" value={form.issuedDate || ''} onChange={e => set('issuedDate', e.target.value)} />
      </div>
      <div className="col-12">
        <label className="form-label fw-semibold">Achievement (optional)</label>
        <input className="form-control" value={form.achievement || ''} onChange={e => set('achievement', e.target.value)} placeholder="e.g. With Distinction, Top Performer, Best Project" />
      </div>
      <div className="col-12">
        <label className="form-label fw-semibold">Certificate Image <span className="text-danger">*</span></label>
        <ImageUpload value={form.certificateImageUrl || ''} onChange={v => set('certificateImageUrl', v)} label="Certificate" />
        <div className="form-text">Upload a JPG/PNG image of the certificate. Recommended: A4 landscape (1122 × 794 px).</div>
      </div>
      <div className="col-md-4">
        <label className="form-label fw-semibold">Order</label>
        <input type="number" className="form-control" value={form.order ?? 0} min={0} onChange={e => set('order', parseInt(e.target.value) || 0)} />
      </div>
      <div className="col-auto d-flex align-items-end">
        <div className="form-check form-switch">
          <input className="form-check-input" type="checkbox" id="certActive" checked={!!form.isActive} onChange={e => set('isActive', e.target.checked)} />
          <label className="form-check-label" htmlFor="certActive">Active</label>
        </div>
      </div>
    </div>
  );
}

// ─── Item Cards ───────────────────────────────────────────────────────────────
function ItemActions({ item, onEdit, onDelete, onToggle, canWrite, canDelete }) {
  return (
    <div className="d-flex gap-1 mt-2">
      {canWrite && (
        <button className="btn btn-sm btn-outline-secondary flex-fill" onClick={onEdit}>
          <i className="bi bi-pencil me-1" />Edit
        </button>
      )}
      {canWrite && (
        <button className={`btn btn-sm ${item.isActive ? 'btn-outline-warning' : 'btn-outline-success'}`}
          onClick={onToggle} title={item.isActive ? 'Deactivate' : 'Activate'}>
          <i className={`bi ${item.isActive ? 'bi-eye-slash' : 'bi-eye'}`} />
        </button>
      )}
      {canDelete && (
        <button className="btn btn-sm btn-outline-danger" onClick={onDelete}>
          <i className="bi bi-trash" />
        </button>
      )}
    </div>
  );
}

function SlideCard({ item, ...actions }) {
  const imgSrc = uploadUrl(item.imageUrl);
  return (
    <div className={`card h-100 ${!item.isActive ? 'opacity-60' : ''}`} style={{ borderRadius: 12 }}>
      <div style={{ position: 'relative', height: 130, borderRadius: '12px 12px 0 0', overflow: 'hidden', background: item.bgColor || '#1e293b' }}>
        {imgSrc && <img src={imgSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={e => { e.target.style.display = 'none'; }} />}
        {!item.isActive && (
          <div style={{ position: 'absolute', top: 6, right: 6 }}>
            <span className="badge bg-secondary">Inactive</span>
          </div>
        )}
        {item.title && (
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '16px 10px 8px', background: 'linear-gradient(transparent, rgba(0,0,0,0.65))' }}>
            <div style={{ color: 'white', fontWeight: 700, fontSize: 13 }}>{item.title}</div>
            {item.subtitle && <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11 }}>{item.subtitle}</div>}
          </div>
        )}
      </div>
      <div className="card-body py-2 px-2">
        <div className="text-muted small">Order: {item.order ?? 0}{item.linkUrl ? ' · Has link' : ''}</div>
        <ItemActions item={item} {...actions} />
      </div>
    </div>
  );
}

function ReviewCard({ item, ...actions }) {
  const imgSrc = uploadUrl(item.thumbnailUrl);
  return (
    <div className={`card h-100 ${!item.isActive ? 'opacity-60' : ''}`} style={{ borderRadius: 12 }}>
      <div style={{ position: 'relative', paddingBottom: '52%', background: '#0f172a', borderRadius: '12px 12px 0 0', overflow: 'hidden' }}>
        {imgSrc
          ? <img src={imgSrc} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.75 }} onError={e => { e.target.style.display = 'none'; }} />
          : <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #6366f1, #ec4899)' }} />
        }
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="bi bi-play-fill text-danger" style={{ fontSize: 18, marginLeft: 2 }} />
          </div>
        </div>
        {!item.isActive && (
          <div style={{ position: 'absolute', top: 6, right: 6 }}><span className="badge bg-secondary">Inactive</span></div>
        )}
      </div>
      <div className="card-body py-2 px-2">
        <div className="fw-semibold" style={{ fontSize: 13 }}>{item.reviewerName || '—'}</div>
        <div className="text-muted" style={{ fontSize: 11 }}>{item.reviewerCourse || ''}{item.reviewerBatch ? ` · ${item.reviewerBatch}` : ''}</div>
        {(item.rating || 0) > 0 && <div style={{ fontSize: 12, color: '#f59e0b' }}>{'★'.repeat(item.rating)}{'☆'.repeat(5 - item.rating)}</div>}
        <ItemActions item={item} {...actions} />
      </div>
    </div>
  );
}

function PromoCard({ item, ...actions }) {
  const imgSrc = uploadUrl(item.imageUrl);
  return (
    <div className={`card h-100 ${!item.isActive ? 'opacity-60' : ''}`} style={{ borderRadius: 12 }}>
      {imgSrc && (
        <img src={imgSrc} alt="" style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: '12px 12px 0 0' }}
          onError={e => { e.target.style.display = 'none'; }} />
      )}
      <div className="card-body py-2 px-2">
        {!item.isActive && <span className="badge bg-secondary mb-1">Inactive</span>}
        {item.promoTitle && <div className="fw-semibold" style={{ fontSize: 13 }}>{item.promoTitle}</div>}
        {item.promoText && <div className="text-muted" style={{ fontSize: 11 }}>{item.promoText.slice(0, 80)}{item.promoText.length > 80 ? '…' : ''}</div>}
        {item.promoVideoUrl && <div className="text-muted small mt-1"><i className="bi bi-play-circle me-1" />Has promo video</div>}
        <ItemActions item={item} {...actions} />
      </div>
    </div>
  );
}

function LearningEventCard({ item, ...actions }) {
  const imgSrc = uploadUrl(item.imageUrl);
  const labels = { free_live_class: 'Free Live Class', workshop: 'Workshop', webinar: 'Webinar', event: 'Event' };
  const date = item.eventDate ? new Date(item.eventDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Date not set';
  return (
    <div className={`card h-100 ${!item.isActive ? 'opacity-60' : ''}`} style={{ borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ aspectRatio: '16 / 9', background: 'linear-gradient(135deg, #4338ca, #0ea5e9)' }}>
        {imgSrc && <img src={imgSrc} alt={item.promoTitle || 'Learning event'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.currentTarget.style.display = 'none'; }} />}
      </div>
      <div className="card-body py-2 px-2">
        <div className="d-flex justify-content-between gap-2 mb-1">
          <span className="badge bg-primary-subtle text-primary">{labels[item.eventKind] || 'Event'}</span>
          {!item.isActive && <span className="badge bg-secondary">Inactive</span>}
        </div>
        <div className="fw-semibold" style={{ fontSize: 13 }}>{item.promoTitle || 'Untitled event'}</div>
        <div className="text-muted" style={{ fontSize: 11 }}><i className="bi bi-calendar3 me-1" />{date}{item.startTime ? ` at ${item.startTime}` : ''}</div>
        <div className="text-muted text-capitalize" style={{ fontSize: 11 }}><i className="bi bi-broadcast me-1" />{item.deliveryMode || 'online'}</div>
        <ItemActions item={item} {...actions} />
      </div>
    </div>
  );
}

function CertCard({ item, ...actions }) {
  const imgSrc = uploadUrl(item.certificateImageUrl);
  return (
    <div className={`card h-100 ${!item.isActive ? 'opacity-60' : ''}`} style={{ borderRadius: 12 }}>
      <div style={{ position: 'relative', height: 120, borderRadius: '12px 12px 0 0', overflow: 'hidden', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
        {imgSrc
          ? <img src={imgSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
          : <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="bi bi-award-fill" style={{ fontSize: 40, color: 'rgba(255,255,255,0.7)' }} />
            </div>
        }
        {!item.isActive && (
          <div style={{ position: 'absolute', top: 6, right: 6 }}><span className="badge bg-secondary">Inactive</span></div>
        )}
      </div>
      <div className="card-body py-2 px-2">
        <div className="fw-semibold" style={{ fontSize: 12, lineHeight: 1.3 }}>{item.studentName || '—'}</div>
        <div className="text-muted" style={{ fontSize: 11 }}>{item.courseName || ''}{item.batchName ? ` · ${item.batchName}` : ''}</div>
        {item.achievement && <span className="badge bg-warning-subtle text-warning border border-warning-subtle mt-1" style={{ fontSize: 9 }}>{item.achievement}</span>}
        {item.issuedDate && <div className="text-muted" style={{ fontSize: 10, marginTop: 2 }}><i className="bi bi-calendar3 me-1" />{new Date(item.issuedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>}
        <ItemActions item={item} {...actions} />
      </div>
    </div>
  );
}

// ─── Blank forms ──────────────────────────────────────────────────────────────
const BLANKS = {
  carousel_slide:       { type: 'carousel_slide', imageUrl: '', title: '', subtitle: '', linkUrl: '', bgColor: '#1e293b', isActive: true, order: 0 },
  video_review:         { type: 'video_review', reviewerName: '', reviewerCourse: '', reviewerBatch: '', videoUrl: '', thumbnailUrl: '', reviewText: '', rating: 5, isActive: true, order: 0 },
  promotion:            { type: 'promotion', imageUrl: '', promoTitle: '', promoText: '', promoVideoUrl: '', ctaText: '', ctaLink: '', isActive: true, order: 0 },
  certificate_showcase: { type: 'certificate_showcase', studentName: '', courseName: '', batchName: '', certificateImageUrl: '', issuedDate: '', achievement: '', isActive: true, order: 0 },
  learning_event:       { type: 'learning_event', eventKind: 'free_live_class', promoTitle: '', promoText: '', imageUrl: '', eventDate: '', startTime: '', endTime: '', deliveryMode: 'online', meetingUrl: '', venue: '', capacity: 0, registrationUrl: '', isActive: true, order: 0 },
};

const TABS = [
  { key: 'carousel_slide',       label: 'Carousel Slides', icon: 'bi-images',        desc: 'Rotating banner images displayed at the top of the student dashboard' },
  { key: 'video_review',         label: 'Video Reviews',   icon: 'bi-play-circle',   desc: 'Student testimonial videos shown in the "What Our Students Say" section' },
  { key: 'promotion',            label: 'Promotions',      icon: 'bi-megaphone',     desc: 'Promotional posters and videos shown to students' },
  { key: 'learning_event',       label: 'Events & Live Classes', icon: 'bi-calendar2-event', desc: 'Publish free live classes, workshops, webinars and events to the correct student sections' },
  { key: 'certificate_showcase', label: 'Certificates',    icon: 'bi-award',         desc: 'Student completion certificate soft copies — scroll automatically on the student dashboard' },
];

// ─── Main Component ───────────────────────────────────────────────────────────
const SHOWCASE_GUIDE = {
  carousel_slide: {
    studentArea: 'Student dashboard top banner slider',
    imageSize: '1600 x 620 px landscape',
    contentRule: 'Image is required. Title and subtitle are optional but recommended.',
    notes: [
      'Keep the main subject in the center area so the mobile crop stays safe.',
      'Use short titles only. Avoid paragraph text inside the form.',
      'Use this for major campaigns, admissions, or top banner updates.'
    ]
  },
  promotion: {
    studentArea: 'Dashboard Career Boost card and promotion placements',
    imageSize: '1200 x 900 px or 4:3 landscape',
    contentRule: 'Image, title, and short text should be added together. Video is optional.',
    notes: [
      'This type is best for one message, one image, and one CTA.',
      'Keep the text short so the student card stays clean.',
      'Leave empty space in the image because dashboard text can sit beside or over it.'
    ]
  },
  video_review: {
    studentArea: 'Student testimonial and showcase sections',
    imageSize: '1280 x 720 px thumbnail',
    contentRule: 'Video URL and student name are required. Course, batch, thumbnail, and quote are recommended.',
    notes: [
      'Use YouTube links for stable playback on web and mobile.',
      'Use a clear thumbnail that still looks good in a small card.',
      'Short quotes work better than long review paragraphs.'
    ]
  },
  certificate_showcase: {
    studentArea: 'Scrolling certificate showcase and student success inserts',
    imageSize: '1122 x 794 px A4 landscape preview',
    contentRule: 'Certificate image, student name, and course name should be filled. Achievement is optional.',
    notes: [
      'Upload a clean certificate preview without large blank margins.',
      'Make sure certificate text is still readable in card view.',
      'Use this for completion proof, rank awards, or major achievements.'
    ]
  },
  learning_event: {
    studentArea: 'Upcoming Live Classes, Free Workshops and Upcoming Events',
    imageSize: '1200 x 675 px (16:9), under 2 MB',
    contentRule: 'Thumbnail, title, description, type, date and start time are required.',
    notes: [
      'Free Live Class appears in Upcoming Live Classes.',
      'Workshop and Webinar appear in Free Workshops.',
      'Event appears in Upcoming Events on the Explore page.'
    ]
  }
};

function ShowcaseUsageGuide({ guide }) {
  if (!guide) return null;
  return (
    <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: 16, background: 'linear-gradient(135deg, #f8fbff 0%, #eef4ff 100%)' }}>
      <div className="card-body p-3 p-lg-4">
        <div className="mb-3">
          <div className="text-uppercase fw-semibold text-primary small mb-1">Usage Guide</div>
          <h6 className="mb-1">Student-side content guidance</h6>
          <div className="text-muted small">Use the correct image size and only the fields needed for this layout.</div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <div className="rounded-3 border bg-white p-3 h-100">
              <div className="text-muted text-uppercase small fw-semibold mb-1">Shown In</div>
              <div className="fw-semibold">{guide.studentArea}</div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="rounded-3 border bg-white p-3 h-100">
              <div className="text-muted text-uppercase small fw-semibold mb-1">Recommended Image</div>
              <div className="fw-semibold">{guide.imageSize}</div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="rounded-3 border bg-white p-3 h-100">
              <div className="text-muted text-uppercase small fw-semibold mb-1">Content Rule</div>
              <div className="fw-semibold" style={{ fontSize: 14 }}>{guide.contentRule}</div>
            </div>
          </div>
        </div>

        <div className="row g-2 mt-2">
          {guide.notes.map((note, index) => (
            <div key={index} className="col-12 col-lg-4">
              <div className="rounded-3 border bg-white p-3 h-100 d-flex gap-2">
                <i className="bi bi-info-circle text-primary mt-1" />
                <div className="small text-secondary">{note}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function PromotionsPage() {
  const { user } = useAuth();
  const role = user?.roleKey || user?.role || '';
  const canWrite  = ['admin', 'manager', 'team_lead'].includes(role) || user?.userType === 'super_admin';
  const canDelete = ['admin'].includes(role) || user?.userType === 'super_admin';

  const [tab,        setTab]        = useState('carousel_slide');
  const [items,      setItems]      = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [showForm,   setShowForm]   = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form,       setForm]       = useState({ ...BLANKS.carousel_slide });
  const [saving,     setSaving]     = useState(false);
  const [alert,      setAlert]      = useState({ show: false, msg: '', type: 'success' });
  const [videoPreview, setVideoPreview] = useState(null);

  const showAlert = (msg, type = 'success') => setAlert({ show: true, msg, type });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/training/promotions/all', { params: { type: tab } });
      setItems(r.data?.records || r.data?.data || []);
    } catch (e) {
      showAlert(e.response?.data?.message || 'Failed to load', 'danger');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setEditTarget(null); setForm({ ...BLANKS[tab] }); setShowForm(true); };
  const openEdit = item => {
    setEditTarget(item);
    setForm({
      ...item,
      eventDate: item.eventDate ? String(item.eventDate).slice(0, 10) : '',
    });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditTarget(null); };

  const handleSave = async () => {
    if (tab === 'learning_event' && (
      !String(form.promoTitle || '').trim()
      || !String(form.promoText || '').trim()
      || !form.imageUrl
      || !form.eventDate
      || !form.startTime
    )) {
      showAlert('Add the title, description, 1200 x 675 thumbnail, date and start time before publishing.', 'danger');
      return;
    }
    setSaving(true);
    try {
      if (editTarget) {
        await api.put(`/training/promotions/${editTarget._id}`, form);
        showAlert('Updated successfully');
      } else {
        await api.post('/training/promotions', { ...form, type: tab });
        showAlert('Created successfully');
      }
      closeForm();
      load();
    } catch (e) {
      showAlert(e.response?.data?.message || 'Save failed', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this item? This cannot be undone.')) return;
    try {
      await api.delete(`/training/promotions/${id}`);
      showAlert('Deleted');
      load();
    } catch (e) {
      showAlert(e.response?.data?.message || 'Delete failed', 'danger');
    }
  };

  const toggleActive = async item => {
    try {
      await api.put(`/training/promotions/${item._id}`, { isActive: !item.isActive });
      load();
    } catch (e) {
      showAlert('Update failed', 'danger');
    }
  };

  const currentTab = TABS.find(t => t.key === tab);
  const currentGuide = SHOWCASE_GUIDE[tab];

  return (
    <div>
      <PageHeader
        title="Student Showcase"
        subtitle="Manage what students see on their dashboard — banner slides, video reviews, and promotions"
      />
      <AlertToast {...alert} onClose={() => setAlert(a => ({ ...a, show: false }))} />

      {/* Tab bar */}
      <div className="d-flex gap-2 mb-1 flex-wrap">
        {TABS.map(t => (
          <button key={t.key}
            className={`btn btn-sm d-flex align-items-center gap-1 ${tab === t.key ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setTab(t.key)}>
            <i className={`bi ${t.icon}`} />{t.label}
            <span className="badge bg-white text-primary ms-1" style={{ fontSize: 10 }}>
              {tab === t.key ? items.length : ''}
            </span>
          </button>
        ))}
      </div>
      <div className="text-muted small mb-3" style={{ paddingLeft: 2 }}>{currentTab?.desc}</div>
      <ShowcaseUsageGuide guide={currentGuide} />

      {/* Add button */}
      {canWrite && (
        <div className="d-flex justify-content-end mb-3">
          <button className="btn btn-success btn-sm" onClick={openAdd}>
            <i className="bi bi-plus-lg me-1" />Add {currentTab?.label.replace(/s$/, '')}
          </button>
        </div>
      )}

      {/* Item grid */}
      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-5" style={{ color: '#94a3b8' }}>
          <i className={`bi ${currentTab?.icon} d-block mb-2`} style={{ fontSize: 44, opacity: 0.25 }} />
          <div className="fw-semibold">No {currentTab?.label.toLowerCase()} yet</div>
          <div className="small mb-3">Add one to start showing content to students</div>
          {canWrite && (
            <button className="btn btn-primary btn-sm" onClick={openAdd}>
              <i className="bi bi-plus me-1" />Add now
            </button>
          )}
        </div>
      ) : (
        <div className="row g-3">
          {items.map(item => {
            const shared = {
              onEdit:   () => openEdit(item),
              onDelete: () => handleDelete(item._id),
              onToggle: () => toggleActive(item),
              canWrite, canDelete,
            };
            return (
              <div key={item._id} className="col-6 col-md-4 col-lg-3">
                {tab === 'carousel_slide'       && <SlideCard item={item} {...shared} />}
                {tab === 'video_review'         && <ReviewCard item={item} {...shared} onPreview={() => setVideoPreview(item)} />}
                {tab === 'promotion'            && <PromoCard item={item} {...shared} onPreview={() => setVideoPreview(item)} />}
                {tab === 'certificate_showcase' && <CertCard item={item} {...shared} />}
                {tab === 'learning_event'       && <LearningEventCard item={item} {...shared} />}
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.45)' }} onClick={closeForm}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-0">
                    {editTarget ? 'Edit' : 'Add'} {currentTab?.label.replace(/s$/, '')}
                  </h5>
                  <small className="text-muted">{currentTab?.desc}</small>
                </div>
                <button className="btn-close" onClick={closeForm} />
              </div>
              <div className="modal-body">
                {tab === 'carousel_slide'       && <CarouselForm form={form} onChange={setForm} />}
                {tab === 'video_review'         && <VideoReviewForm form={form} onChange={setForm} />}
                {tab === 'promotion'            && <PromotionForm form={form} onChange={setForm} />}
                {tab === 'certificate_showcase' && <CertificateForm form={form} onChange={setForm} />}
                {tab === 'learning_event'       && <LearningEventForm form={form} onChange={setForm} />}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={closeForm}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving && <span className="spinner-border spinner-border-sm me-1" />}
                  {editTarget ? 'Save Changes' : 'Publish'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Preview Modal */}
      {videoPreview && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.75)' }} onClick={() => setVideoPreview(null)}>
          <div className="modal-dialog modal-lg modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content border-0">
              <div className="modal-header border-0">
                <h6 className="modal-title">{videoPreview.reviewerName || videoPreview.promoTitle || 'Preview'}</h6>
                <button className="btn-close" onClick={() => setVideoPreview(null)} />
              </div>
              <div className="modal-body p-0">
                {(videoPreview.videoUrl || videoPreview.promoVideoUrl) && (
                  <div style={{ position: 'relative', paddingBottom: '56.25%' }}>
                    <iframe src={toEmbedUrl(videoPreview.videoUrl || videoPreview.promoVideoUrl)}
                      title="preview" allowFullScreen
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
